import { useMemo } from 'react'
import { useRecentViewsQuery } from '@entities/recent-views'
import { useCards } from '@/hooks/useApi'
import type { Card } from '@/types/api'
import { useNavigate } from 'react-router-dom'
import { CoverCard } from '@shared/ui/CoverCard'
import { ROUTES } from '@shared/config/routes'
import FavoriteButton from '@/pages/HomePage/components/FavoriteButton'

/**
 * Блок «Недавно просмотренные» на главной.
 *
 * Бэк отдаёт только идентификаторы и время открытия — названия и картинки
 * берём из уже загруженного списка карточек. Порядок задаёт бэк, от свежих к
 * старым, на клиенте не пересортировываем.
 *
 * Блока не видно, когда показывать нечего: список пуст, ещё грузится, не
 * загрузился или все карточки оказались недоступны. Пустой заголовок над
 * пустотой выглядит поломкой, а ошибку отдельного блока незачем выносить на
 * весь экран.
 */
export const RecentlyViewed = () => {
  const navigate = useNavigate()
  const { data: recentData } = useRecentViewsQuery()
  const { data: cardsData } = useCards()

  const cards = useMemo<Card[]>(() => {
    const recent = recentData?.recent ?? []
    if (recent.length === 0) return []

    const byId = new Map<number, Card>(
      (cardsData?.cards ?? []).map(card => [card.card_id, card])
    )

    // Карточка могла уехать из выдачи: скрыли, удалили или у человека
    // кончилась подписка. Такие позиции просто пропускаем.
    return recent
      .map(item => byId.get(item.card_id))
      .filter((card): card is Card => card !== undefined)
  }, [recentData, cardsData])

  if (cards.length === 0) return null

  // Вёрстку блока держит главная: заголовок, отступы и лента у всех блоков
  // общие, а своя разметка здесь разъехалась бы с соседями.
  return (
    <section className="home-block home-block--muted">
      <h2 className="home-block__title">Недавно просмотренное</h2>
      <div className="home-block__scroller">
        {cards.map(card => (
          <CoverCard
            key={card.card_id}
            title={card.title}
            image={card.icon_url}
            action={<FavoriteButton cardId={card.card_id} />}
            onClick={() => navigate(ROUTES.CARD(card.card_id))}
          />
        ))}
      </div>
    </section>
  )
}
